import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Sparkles, Loader2, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";

const STEPS = ["Choose Content", "Choose Photos", "Style & Privacy", "Generate"];

/* Create Travel Story wizard — Private Diary -> Privacy Shield -> Publishing Draft. */
export default function StoryWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [src, setSrc] = useState(null);
  const [step, setStep] = useState(0);
  const [dates, setDates] = useState(new Set());
  const [photos, setPhotos] = useState(new Set());
  const [style, setStyle] = useState("Travel Blog");
  const [author, setAuthor] = useState("");
  const [dateMode, setDateMode] = useState("month");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/stories/diary-content/${id}`).then((r) => {
      setSrc(r.data);
      setDates(new Set(r.data.days.filter((d) => d.has_content).map((d) => d.date)));
    }).catch(() => toast.error("Could not load your diary"));
  }, [id]);

  if (!src) return <div className="max-w-4xl mx-auto px-5 py-16 text-sm text-muted-foreground">Loading your diary…</div>;
  const token = localStorage.getItem("token") || "";
  const toggle = (set, setter, v) => { const n = new Set(set); n.has(v) ? n.delete(v) : n.add(v); setter(n); };

  const generate = async () => {
    if (dates.size === 0) { toast.error("Select at least one day"); setStep(0); return; }
    setBusy(true);
    try {
      const sel = src.photos.filter((p) => photos.has(p.pid)).map((p) => ({ kind: p.kind, iid: p.iid, pid: p.pid }));
      const r = await api.post(`/stories/generate/${id}`, {
        dates: [...dates], style, photos: sel, author_display: author, date_display_mode: dateMode,
      });
      const n = (r.data.privacy_findings || []).length;
      toast.success(n > 0 ? `Draft created — Privacy Shield removed ${n} sensitive item${n === 1 ? "" : "s"}` : "Draft created — no sensitive information detected");
      nav(`/stories/${r.data.story.id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate story");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8" data-testid="story-wizard">
      <Link to={`/trips/${id}/diary`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-3"><ArrowLeft className="w-4 h-4" /> Back to diary</Link>
      <div className="border border-zinc-300 bg-gradient-to-br from-fuchsia-600 to-indigo-700 text-white p-6 mb-5">
        <div className="text-xs font-mono uppercase tracking-wider opacity-80 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Create Travel Story / Blog / PDF</div>
        <h1 className="font-heading font-900 text-3xl mt-1">{src.trip.title}</h1>
        <p className="text-sm opacity-90 mt-1 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Privacy Shield removes booking numbers, emails, phone numbers and documents before anything is generated or shared. Your private diary is never changed.</p>
      </div>

      <div className="flex gap-1 mb-6">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => i < 3 && setStep(i)} data-testid={`wizard-step-${i}`}
            className={`flex-1 text-xs font-700 py-2 border-b-2 ${i === step ? "border-indigo-600 text-indigo-700" : i < step ? "border-emerald-400 text-emerald-700" : "border-zinc-200 text-muted-foreground"}`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      {step === 0 && (
        <div data-testid="wizard-content-step">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-900 text-lg">Which days should be part of the story?</h2>
            <button onClick={() => setDates(new Set(src.days.filter((d) => d.has_content).map((d) => d.date)))} className="text-xs text-primary hover:underline" data-testid="wizard-select-content-days">Select days with content</button>
          </div>
          <div className="space-y-1.5">
            {src.days.map((d) => (
              <label key={d.date} className={`flex items-start gap-3 border px-3 py-2 cursor-pointer ${dates.has(d.date) ? "border-indigo-400 bg-indigo-50/50" : "border-zinc-200 bg-white"}`} data-testid={`wizard-day-${d.date}`}>
                <input type="checkbox" checked={dates.has(d.date)} onChange={() => toggle(dates, setDates, d.date)} className="mt-1" data-testid={`wizard-day-check-${d.date}`} />
                <div className="min-w-0">
                  <div className="text-sm font-600">Day {d.day_no} — {d.weekday}, {d.date}{d.city ? ` · ${d.city}` : ""}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {d.entry_preview || (d.activities.length ? d.activities.join(", ") : "No diary content this day")}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <button onClick={() => setStep(1)} data-testid="wizard-next-1" className="mt-4 px-5 py-2 bg-indigo-600 text-white text-sm font-700">Next — Choose Photos</button>
        </div>
      )}

      {step === 1 && (
        <div data-testid="wizard-photos-step">
          <h2 className="font-heading font-900 text-lg mb-1">Choose photos ({photos.size} selected)</h2>
          <p className="text-xs text-muted-foreground mb-3">Only photos you explicitly select are ever used. A public-safe copy is created — location data (GPS/EXIF) is stripped automatically.</p>
          {src.photos.length === 0 ? (
            <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-6 text-center">No diary photos yet — you can add photos to activities and diary entries from the diary, or continue without photos.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {src.photos.map((p) => (
                <button key={p.pid} onClick={() => toggle(photos, setPhotos, p.pid)} data-testid={`wizard-photo-${p.pid}`}
                  className={`relative border-2 ${photos.has(p.pid) ? "border-indigo-600" : "border-transparent"}`}>
                  <img src={`${API}${p.url.replace("/api", "")}?auth=${token}`} alt={p.title || ""} className="w-full h-28 object-cover" loading="lazy" />
                  {photos.has(p.pid) && <span className="absolute top-1 right-1 bg-indigo-600 text-white p-0.5"><Check className="w-3.5 h-3.5" /></span>}
                  <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1 py-0.5 truncate text-left"><ImageIcon className="w-2.5 h-2.5 inline mr-0.5" />{p.date}</span>
                </button>
              ))}
            </div>
          )}
          <button onClick={() => setStep(2)} data-testid="wizard-next-2" className="mt-4 px-5 py-2 bg-indigo-600 text-white text-sm font-700">Next — Style &amp; Privacy</button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5" data-testid="wizard-style-step">
          <div>
            <h2 className="font-heading font-900 text-lg mb-2">Story style</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {src.styles.map((s) => (
                <button key={s} onClick={() => setStyle(s)} data-testid={`wizard-style-${s.replace(/\s/g, "-")}`}
                  className={`border px-3 py-2.5 text-sm font-600 text-left ${style === s ? "border-indigo-600 bg-indigo-50 text-indigo-800" : "border-zinc-300 bg-white"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-heading font-900 text-lg mb-2">Author shown on the story</h2>
            <input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="e.g. Thomas V. — leave empty for “Anonymous Traveler”" data-testid="wizard-author"
              className="w-full max-w-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white" />
            <p className="text-xs text-muted-foreground mt-1">Your account's legal name is never published automatically.</p>
          </div>
          <div>
            <h2 className="font-heading font-900 text-lg mb-2">Travel dates shown publicly</h2>
            <div className="flex flex-wrap gap-2">
              {[["exact", "Exact dates"], ["month", "Month only (recommended)"], ["year", "Year only"], ["hidden", "Hide dates"]].map(([v, l]) => (
                <button key={v} onClick={() => setDateMode(v)} data-testid={`wizard-datemode-${v}`}
                  className={`border px-3 py-2 text-sm font-600 ${dateMode === v ? "border-indigo-600 bg-indigo-50 text-indigo-800" : "border-zinc-300 bg-white"}`}>{l}</button>
              ))}
            </div>
            {src.trip.status !== "completed" && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mt-2" data-testid="wizard-active-trip-warning">
                This trip is not finished yet — consider hiding exact dates and upcoming details when sharing during active travel.
              </p>
            )}
          </div>
          <button onClick={() => setStep(3)} data-testid="wizard-next-3" className="px-5 py-2 bg-indigo-600 text-white text-sm font-700">Next — Review &amp; Generate</button>
        </div>
      )}

      {step === 3 && (
        <div className="border border-zinc-300 bg-white p-6" data-testid="wizard-generate-step">
          <h2 className="font-heading font-900 text-lg mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Ready to create your draft</h2>
          <ul className="text-sm space-y-1.5 mb-4">
            <li>• <b>{dates.size}</b> day{dates.size === 1 ? "" : "s"} selected · <b>{photos.size}</b> photo{photos.size === 1 ? "" : "s"} · style: <b>{style}</b></li>
            <li>• Privacy Shield scans everything and removes booking numbers, emails, phones, documents and QR codes.</li>
            <li>• AI only uses your own diary material — it never invents experiences.</li>
            <li>• The draft stays <b>private</b> until you explicitly press “Publish Story”.</li>
          </ul>
          <button onClick={generate} disabled={busy} data-testid="wizard-generate" className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white text-sm font-700 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Creating your story…" : "Create My Story with AI"}
          </button>
        </div>
      )}
    </div>
  );
}
